import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AppDialogRef } from '../shared/components/dialog/dialog-ref';
import { DialogService } from '../shared/components/dialog/dialog.service';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { openCopilotSnackbar } from '../shared/components/copilot-snackbar/open-copilot-snackbar';
import { Dictation, Note } from '../_interfaces/dictation';
import { ApiService } from '../_services/api.service';
import { AuthService } from '../_services/auth.service';
import { BrandingService } from '../_services/branding.service';
import { MAX_DICTATION_DURATION_SECONDS, RecorderService } from '../_services/recorder.service';
import { RecordingState } from '../_enums/recording-state.enum';
import { formatDictationDurationLimit, formatDictationSubjectName } from '../_helpers/utils';
import { RecorderErrorCode, RECORDER_DEFAUL_ERROR_MESSAGE, errorMEssageByCode } from '../_services/recorder-errors';
import { Subject, takeUntil, distinctUntilChanged, filter, skip, take } from 'rxjs';
import { User } from '../_interfaces/user';
import {
  applyLiveTranscriptResult,
  buildTranscriptionFromRawTranscription,
  mapUtterancesToTranscript,
  TranscriptUtterance,
} from '../_helpers/transcription';
import { isResumeOriginAllowed, isResumedConsultation } from '../document/helpers/resume-consultation';
import { SelectTemplateComponent } from '../shared/dialogs/select-template/select-template.component';
import { UpdateDocumentsComponent } from './components/update-documents/update-documents.component';
import { ContentPanelComponent, CONTENT_PANEL_COLLAPSED_HEIGHT_PX } from './components/content-panel/content-panel.component';
import { ConfirmComponent, ConfirmDialogData } from '../shared/dialogs/confirm/confirm.component';
import { EntitySettings } from '../_interfaces/entity-settings.interface';
import { UserSettings } from '../_interfaces/user-settings.interface';
import { EditPatientComponent } from '../_dialogs/edit-patient/edit-patient.component';
import { Template } from '../_interfaces/template';
import { ManualPatientSelectorComponent } from '../_dialogs/manual-patient-selector/manual-patient-selector.component';
import { ToastProvider } from '../shared/components/toast/toast.provider';
import { ConnectionBannerComponent } from '../shared/components/connection-banner/connection-banner.component';
import { FeatureFlagsService } from '../_services/feature-flags.service';
import { AnalyticsService } from '../_services/analytics.service';
import { ScreenWakeLockDirective, WakeLockUnavailableReason } from '../shared/directives/screen-wake-lock.directive';
import { nextContentUpdatedAt } from '../_helpers/content-updated-at';
import { getDictationRetention } from '../_helpers/retention';
import { FrontendResult } from '../_interfaces/frontend-result';

type EncounterUiState = 'initial' | 'recording' | 'paused';

@Component({
  selector: 'app-encounter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatSidenavModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslateModule,
    ConnectionBannerComponent,
    ContentPanelComponent,
    ScreenWakeLockDirective,
  ],
  templateUrl: './encounter.component.html',
  styleUrl: './encounter.component.scss',
})
export class EncounterComponent implements OnInit, OnDestroy {
  private _audioVisualizer: ElementRef<HTMLCanvasElement> | undefined;
  @ViewChild('audioVisualizer') set audioVisualizer(el: ElementRef<HTMLCanvasElement> | undefined) {
    this._audioVisualizer = el;
    if (this._audioVisualizer) {
      this.initializeCanvas();
      this.startVisualization();
    }
  }
  @ViewChild('rightDrawer', { static: false }) rightDrawer!: MatSidenav;
  @ViewChild(ContentPanelComponent) private contentPanel?: ContentPanelComponent;

  // Context notes container and textarea for resize handling
  private _contextNotesContainer?: ElementRef<HTMLElement>;
  private _noteTextarea?: ElementRef<HTMLTextAreaElement>;
  private contextResizeObserver?: ResizeObserver;

  @ViewChild('contextNotesContainer') set contextNotesContainer(el: ElementRef<HTMLElement> | undefined) {
    if (this._contextNotesContainer && this.contextResizeObserver) {
      this.contextResizeObserver.unobserve(this._contextNotesContainer.nativeElement);
    }
    this._contextNotesContainer = el;

    if (!this.contextResizeObserver && typeof ResizeObserver !== 'undefined') {
      this.contextResizeObserver = new ResizeObserver(() => {
        const textarea = this._noteTextarea?.nativeElement;
        const container = this._contextNotesContainer?.nativeElement;
        if (textarea && container) {
          this.autoResize(textarea, container);
        }
      });
    }

    if (this._contextNotesContainer && this.contextResizeObserver) {
      this.contextResizeObserver.observe(this._contextNotesContainer.nativeElement);
    }
  }

  @ViewChild('noteTextarea') set noteTextarea(el: ElementRef<HTMLTextAreaElement> | undefined) {
    this._noteTextarea = el;
  }

  private _notesListContainer?: ElementRef<HTMLElement>;

  @ViewChild('notesListContainer') set notesListContainer(el: ElementRef<HTMLElement> | undefined) {
    this._notesListContainer = el;
  }

  // Panels handled by drawer now
  notes: Array<Note> = [];
  newNote = '';
  transcript: string[] = [];
  transcriptUtterances: TranscriptUtterance[] = [];
  showEncounterContentPanel = false;
  contentPanelTabIndex = 1;
  readonly contentPanelCollapsedHeightPx = CONTENT_PANEL_COLLAPSED_HEIGHT_PX;
  dictation: Dictation | null = null;
  dictationId: string | null = null;
  chunkDuration: number = 0.2;

  // New properties for the UI
  patientConsentGiven: boolean = false;
  requirePatientConsent: boolean = false;
  patientConsentMessage: string = '';
  selectedMicrophone: string = '';
  availableMicrophones: MediaDeviceInfo[] = [];
  isMicSelectorExpanded = false;
  isMobile = false;
  recorderLoading: boolean = false;
  finishingEncounter: boolean = false;
  stoppingEncounter: boolean = false;
  resumingEncounter: boolean = false;
  startingEncounter: boolean = false;

  serverReady: boolean = false;
  private totalTime: number = 0;
  private currentRecordingTime: number = 0; // Track current recording session time
  // recording index and timers handled by service now
  // Audio visualization properties
  private animationId: number | null = null;
  private canvasContext: CanvasRenderingContext2D | null = null;
  private currentAmplitude: number = 0;
  /** Master width for the audio visualizer; canvas height and bar layout derive from this. */
  readonly visualizerWidth = 120;
  readonly visualizerHeight = 60;
  private readonly barCount = 12;
  readonly barSpacing = 4;
  pausedWaveformBars: number[] = Array.from({ length: this.barCount }, (_, i) => i);

  get visualizerBarWidth(): number {
    return (this.visualizerWidth - this.barSpacing * (this.barCount - 1)) / this.barCount;
  }
  // State helpers
  recordingState: RecordingState = RecordingState.Disconnected;

  get screenWakeLockActive(): boolean {
    return this.recordingState === RecordingState.Recording;
  }

  onWakeLockUnavailable(reason: WakeLockUnavailableReason): void {
    this.analytics.track('screen_wake_lock_unavailable', { reason });
  }

  private destroy$ = new Subject<void>();
  private breakpointObserver = inject(BreakpointObserver);
  private destroyed = false;
  private openDialogs: AppDialogRef<any>[] = [];

  // Drawer state
  activeDrawer: 'transcript' | 'context' | null = null;
  activeNotesTab: 'transcript' | 'context' = 'context';
  isContextAccordionExpanded: boolean = false;
  isResumedConsultation = false;
  private shouldAutoStartOnResume = false;

  user: User | null = null;
  isLegalOrg = false;
  currentError: { message: string; errorCode?: string } | null = null;
  networkInterruptionOccurred = false;
  durationLimitReached: boolean = false;
  private durationLimitReachedOnLoad = true;
  private currentErrorToastId: string | null = null;
  private syncingToastId: string | null = null;
  // browserOnline: boolean = true;
  isInitializingRecorder: boolean = true;

  private get userActionInProgress(): boolean {
    return this.startingEncounter || this.resumingEncounter || this.stoppingEncounter || this.finishingEncounter;
  }
  private recordingInterrupted: boolean = false;
  private waitingForFinalResult: boolean = false;
  private activeTranscriptUtteranceIndex: number | null = null;

  private readonly analytics = inject(AnalyticsService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private authService: AuthService,
    private recorderService: RecorderService,
    private dialog: DialogService,
    private toastProvider: ToastProvider,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private branding: BrandingService,
    private flags: FeatureFlagsService,
  ) {
    this.user = this.authService.getUser() as User;
    this.isLegalOrg = this.user?.entity?.settings?.category === 'Legal';
    this.dictationId = this.route.snapshot.paramMap.get('_id');
  }

  get duration(): number {
    return this.dictation?.duration || 0;
  }

  get canBeginConsult(): boolean {
    const consentGranted = this.patientConsentGiven || !this.requirePatientConsent;
    const hasMicrophoneEnabled = !!this.selectedMicrophone;
    return consentGranted && hasMicrophoneEnabled && !this.micAccessBlocked;
  }

  /**
   * @description True when the microphone is in any error state (no devices at all
   * or any MIC_* error). Drives the visual error highlight on the selector.
   */
  get micHasError(): boolean {
    return this.availableMicrophones.length === 0
      || (this.currentError?.errorCode?.startsWith('MIC_') ?? false);
  }

  /**
   * @description True only when the microphone is hard-blocked and the picker
   * cannot help the user recover: permission was denied, no device could be found,
   * or there are no microphones at all. Other MIC_* errors (e.g. the selected
   * device is unavailable but alternatives exist) are recoverable, so the picker
   * stays interactive and the consult buttons are not force-disabled. Also used by
   * the selector label and the error toast so both stay consistent.
   */
  get micAccessBlocked(): boolean {
    return this.currentError?.errorCode === RecorderErrorCode.MIC_PERMISSION_DENIED
      || this.currentError?.errorCode === RecorderErrorCode.MIC_NOT_FOUND
      || this.availableMicrophones.length === 0;
  }

  get micSelectorLabel(): string {
    if (this.micAccessBlocked) {
      return 'Microphone access denied';
    }

    return this.currentError?.message ?? 'Microphone access denied';
  }

  get selectedMicrophoneLabel(): string {
    const selectedDevice = this.availableMicrophones.find(mic => mic.deviceId === this.selectedMicrophone);
    return selectedDevice?.label || this.translate.instant('Select microphone');
  }

  get orderedMicrophones(): MediaDeviceInfo[] {
    const selected = this.availableMicrophones.filter(mic => mic.deviceId === this.selectedMicrophone);
    const rest = this.availableMicrophones.filter(mic => mic.deviceId !== this.selectedMicrophone);
    return [...selected, ...rest];
  }

  async ngOnInit(): Promise<void> {
    this.isInitializingRecorder = true;
    this.notes = [];
    this.showEncounterContentPanel = this.flags.isEnabled('contentPanel');

    const navigationState =
      this.router.getCurrentNavigation()?.extras?.state || (history.state as Record<string, unknown> | null);
    this.shouldAutoStartOnResume = navigationState?.['resumeSession'] === true;
    if (this.shouldAutoStartOnResume) {
      window.history.replaceState({ ...history.state, resumeSession: null }, '');
    }

    this.breakpointObserver.observe('(max-width: 767px)')
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => this.applyMobileBreakpoint(state.matches));

    // Keep the resume block in sync with the service-enforced duration limit.
    this.recorderService.durationLimitReached$.pipe(takeUntil(this.destroy$)).subscribe(reached => {
      const becameReached = !this.durationLimitReached && reached;
      this.durationLimitReached = reached;
      if (becameReached && !this.durationLimitReachedOnLoad) {
        this.trackConsultationDurationLimitReached();
      }
    });

    // Subscribe to recording state changes
    this.recorderService.recordingState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (this.recordingState === RecordingState.Recording && state !== RecordingState.Recording && !this.userActionInProgress) {
        this.recordingInterrupted = true;
      }
      this.recordingState = state;
      this.recorderLoading = state === RecordingState.Loading;

      if (state === RecordingState.Paused || state === RecordingState.Disconnected) {
        this.cleanupAudioVisualization();
      }

      if (state === RecordingState.Recording && this.currentError && !this.currentError.errorCode) {
        this.currentError = null;
        this.updateCurrentErrorToast();
      }
      // Socket.IO reconnect can emit outside the zone, leaving [appScreenWakeLock] stale.
      this.cdr.detectChanges();
    });

    // this.recorderService.browserOnline$.pipe(takeUntil(this.destroy$)).subscribe(isOnline => {
    //   this.browserOnline = isOnline;
    // });

    // Load microphone devices via service
    await this.recorderService.loadMicrophones();

    // Subscribe to available microphones changes
    this.recorderService.availableMicrophones$.pipe(takeUntil(this.destroy$)).subscribe(mics => {
      this.availableMicrophones = mics || [];
      if (this.availableMicrophones.length === 0) {
        this.isMicSelectorExpanded = false;
      }
    });

    // The service owns the source of truth for the active microphone. Mirror its
    // value directly (including a cleared selection when it emits null) so the UI
    // never shows a device the recorder has not actually applied.
    this.recorderService.selectedMicrophone$.pipe(takeUntil(this.destroy$)).subscribe(deviceId => {
      this.selectedMicrophone = deviceId ?? '';
    });

    this.recorderService.isDrainingChunks$.pipe(
      distinctUntilChanged(),
      skip(1),
      takeUntil(this.destroy$)
    ).subscribe(isDraining => {
      if (isDraining) {
        if (this.syncingToastId) {
          this.toastProvider.dismiss(this.syncingToastId);
        }

        this.syncingToastId = this.toastProvider.show({
          message: this.translate.instant('Your connection was unstable. Syncing your audio - please wait.'),
          variant: 'error',
          duration: 0,
        });
      } else {
        if (this.syncingToastId) {
          this.toastProvider.dismiss(this.syncingToastId);
          this.syncingToastId = null;
        }

        this.toastProvider.show({
          message: this.translate.instant('Audio synced. You can continue recording'),
          variant: 'success',
        });
      }
    });

    this.recorderService.errorCode$.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(errorCode => {
      if (!errorCode) {
        this.currentError = null;
        this.updateCurrentErrorToast();
        return;
      }
      console.log('errorCode', errorCode);

      const shouldDisplay = this.isMicrophoneError(errorCode)
        || this.userActionInProgress
        || this.recordingInterrupted;

      this.recordingInterrupted = false;

      if (!shouldDisplay) return;

      this.currentError = { message: this.getRecorderErrorMessage(errorCode), errorCode };
      if (this.isMicrophoneError(errorCode)) {
        this.isMicSelectorExpanded = false;
      }
      if (errorCode === RecorderErrorCode.WS_DISCONNECTED) {
        this.networkInterruptionOccurred = true;
      }
      this.updateCurrentErrorToast();
    });

    // Fetch dictation data from API
    if (!this.dictationId) return;

    try {
      this.dictation = await this.apiService.getDictation(this.dictationId);
    } catch (error) {
      console.error('Error fetching dictation:', error);
      openCopilotSnackbar(this.snackBar, this.translate.instant('Unavailable resource'));
      this.router.navigate(['/home']);
      return;
    }

    // Same rules as Resume: upload / dictation / legacy dictation-like, or audio / transcript deleted → document page
    if (!isResumeOriginAllowed(this.dictation) || !getDictationRetention(this.dictation).canRecord) {
      await this.router.navigate(['/document', this.dictation._id]);
      return;
    }

    if (this.dictation.copilot?.transcription) {
      this.transcript.push(this.dictation.copilot.transcription);
    } else {
      const fromRaw = buildTranscriptionFromRawTranscription(this.dictation.rawTranscription);
      if (fromRaw) {
        this.transcript.push(fromRaw);
      }
    }
    if (this.dictation.copilot?.notes) {
      this.notes = this.dictation.copilot.notes;
    }

    try {
      const rawUtterances = await this.apiService.getUtterances(this.dictationId);
      this.transcriptUtterances = mapUtterancesToTranscript(rawUtterances);
    } catch (error) {
      console.error('Error fetching utterances:', error);
    }

    this.isResumedConsultation = isResumedConsultation(this.dictation);

    await this.initializeRecorder().catch((err) => err);

    if (this.dictation.duration > 0 && this.totalTime === 0) {
      this.totalTime = this.dictation.duration / 1000;
      this.recorderService.setAudioLength(this.totalTime);
      // Mirror timer$ behaviour: prior recording implies consent already given.
      this.patientConsentGiven = true;
    }
    this.durationLimitReachedOnLoad = this.totalTime >= MAX_DICTATION_DURATION_SECONDS;

    this.isInitializingRecorder = false;

    if (this.shouldAutoStartOnResume) {
      this.recorderService.serverReady$.pipe(
        filter(Boolean),
        take(1),
        takeUntil(this.destroy$),
      ).subscribe(() => {
        void this.startRecording();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.closeAllDialogs();
    this.cleanupAudioVisualization();
    this.dismissEncounterToasts();
    this.networkInterruptionOccurred = false;
    this.recorderService.disconnectAndCleanup();
    if (this.contextResizeObserver && this._contextNotesContainer) {
      this.contextResizeObserver.unobserve(this._contextNotesContainer.nativeElement);
      this.contextResizeObserver.disconnect();
      this.contextResizeObserver = undefined;
    }
  }

  /**
   * @description Track a dialog reference to close it when the component is destroyed.
   */
  private trackDialog<T>(dialogRef: AppDialogRef<T>): AppDialogRef<T> {
    this.openDialogs.push(dialogRef);
    dialogRef.afterClosed().subscribe(() => {
      const index = this.openDialogs.indexOf(dialogRef);
      if (index > -1) {
        this.openDialogs.splice(index, 1);
      }
    });
    return dialogRef;
  }

  /**
   * @description Close all open dialogs triggered by this component.
   */
  private closeAllDialogs(): void {
    this.openDialogs.forEach(dialog => this.dialog.closeWithoutAnimation(dialog));
    this.openDialogs = [];
  }

  /**
   * @description Initialise the recorder service.
   */
  private async initializeRecorder(): Promise<void> {
    this.updateConsentSettings();
    // TODO check the nlpAccessKey before initialising the recorder
    if (!this.user?.settings?.nlpAccessKey) {
      console.error('No nlpAccessKey found for user');
      return;
    }

    await this.recorderService.init(this.user.settings.nlpAccessKey, { audio: this.dictation?.audio }).catch((err) => err);

    // Sync observables from service
    this.recorderService.timer$.pipe(takeUntil(this.destroy$)).subscribe(tt => {
      this.totalTime = tt || 0;
      if (this.totalTime > 0) {
        this.patientConsentGiven = true;
      }
    });
    this.recorderService.amplitude$.pipe(takeUntil(this.destroy$)).subscribe(a => {
      // drive visualization from service amplitude
      this.currentAmplitude = a;
    });

    this.recorderService.serverReady$.pipe(takeUntil(this.destroy$)).subscribe(ready => {
      this.serverReady = !!ready;
    });

    this.recorderService.frontendResult$.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result && result.type === 'result') {
        // Defensive: only accept transcripts while this tab is actively recording.
        // The NLP server fans `frontend-result` events to every connection bound
        // to the dictation, so another tab recording the same dictation could
        // otherwise push transcript chunks into this tab's API state.
        // Exception: when we paused but are still waiting for the trailing final
        // result, keep accepting so that last chunk isn't lost.
        if (this.recordingState !== RecordingState.Recording && !this.waitingForFinalResult) {
          return;
        }
        this.handleFrontendResult(result);
      }
    });
  }

  private handleFrontendResult(result: FrontendResult): void {
    this.waitingForFinalResult = !result.final;
    const { utterances, activeIndex, finalChunk } = applyLiveTranscriptResult(
      { utterances: this.transcriptUtterances, activeIndex: this.activeTranscriptUtteranceIndex },
      result,
    );
    this.transcriptUtterances = utterances;
    this.activeTranscriptUtteranceIndex = activeIndex;
    if (finalChunk) {
      this.saveTranscript(finalChunk);
    }
  }

  private saveTranscript(chunk: string): void {
    this.transcript.push(chunk);
    const fullTranscription = this.transcript.join(' ').trim();
    if (this.dictation?.copilot) {
      const watermark = nextContentUpdatedAt(this.dictation.copilot.content_updated_at);
      this.dictation.copilot.transcription = fullTranscription;
      this.dictation.copilot.content_updated_at = watermark;
      this.apiService.updateDictationCopilot(this.dictation._id, {
        copilot: { transcription: fullTranscription, content_updated_at: watermark },
      }).catch(err => console.error('Error updating transcription:', err));
    }
  }

  async startRecording(): Promise<void> {
    if (!this.canBeginConsult || this.recorderLoading) {
      return;
    }
    this.clearError();
    this.startingEncounter = true;
    try {
      await this.recorderService.start(this.selectedMicrophone);
    } catch {
      // Error state is handled by the recorder service's errorCode$ observable.
    } finally {
      this.startingEncounter = false;
    }
    if (!this.isMobile) {
      this.contentPanel?.focusContextInput();
    }
  }


  async pauseRecording(): Promise<void> {
    if (this.stoppingEncounter) return;
    this.stoppingEncounter = true;
    try {
      await this.recorderService.pause();
      this.analytics.track('consultation_paused', {
        encounter_id: this.dictation?._id,
      });
    } finally {
      this.stoppingEncounter = false;
    }
  }

  async resumeRecording(): Promise<void> {
    if (this.resumingEncounter || this.finishingEncounter) return;
    this.clearError();
    this.resumingEncounter = true;
    try {
      await this.recorderService.start(this.selectedMicrophone);
    } finally {
      this.resumingEncounter = false;
    }
  }

  /**
   * @description Wait for a final frontendResult with a timeout
   * @param timeoutMs The timeout in milliseconds
   * @returns Promise that resolves when a final result is received or timeout occurs
   */
  private async waitForFinalResult(timeoutMs: number): Promise<void> {
    // Since the final result is sent asynchronously, we wait for 1 second to ensure the frontendResult is received
    // before proceeding.
    await new Promise(resolve => setTimeout(resolve, 1000));
    return new Promise((resolve) => {
      if (!this.waitingForFinalResult) {
        resolve();
        return;
      }

      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (!this.waitingForFinalResult) {
          clearInterval(checkInterval);
          resolve();
        } else if (Date.now() - startTime >= timeoutMs) {
          clearInterval(checkInterval);
          console.warn('Timeout waiting for final frontendResult');
          resolve();
        }
      }, 200); // Check every 200ms
    });
  }

  async finishEncounter(type: 'recording' | 'paused'): Promise<void> {
    if (this.finishingEncounter || this.recorderLoading) return;
    try {
      this.finishingEncounter = true;
      if (type === 'recording') {
        await this.recorderService.pause();
        await this.waitForFinalResult(10000);
      }
      const transcriptText = (this.transcript.join(' ').trim() || '').trim();
      console.log('Transcript content:', transcriptText);
      const hasTranscriptWithMinLength = transcriptText.length >= 50;

      let hasNotesWithMinLength = false;
      if (!hasTranscriptWithMinLength) {
        const notesContent = (this.notes || []).map(n => (n.note || '').trim()).join(' ').trim();
        console.log('Notes content:', notesContent);
        hasNotesWithMinLength = notesContent.length >= 50;
      }

      if (hasTranscriptWithMinLength || hasNotesWithMinLength) {
        if (this.recordingState === RecordingState.Disconnected) {
          this.currentError = {
            message: this.translate.instant('No internet connection. Please reconnect and try again.'),
          };
          this.updateCurrentErrorToast();
          return;
        }
        try {
          const templates = await this.apiService.getCopilotTemplates();
          if (this.isResumedConsultation) {
            this.openUpdateDocumentsDialog(templates);
          } else {
            this.openTemplateSelector(templates);
          }
        } catch {
          this.currentError = {
            message: this.translate.instant('Unable to load blueprints. Check your connection and try again.'),
          };
          this.updateCurrentErrorToast();
        }
        return;
      }

      if (this.recordingState === RecordingState.Disconnected) {
        this.currentError = {
          message: this.translate.instant('No internet connection. Please reconnect and try again.'),
        };
      } else if (this.recordingState !== RecordingState.Recording) {
        this.currentError = {
          message: this.translate.instant('No enough content to generate documents.'),
        };
      }
      this.updateCurrentErrorToast();
    } catch (error) {
      console.error('Error finishing encounter:', error);
      this.toastProvider.show({
        message: this.translate.instant('An error occurred while finishing.'),
        variant: 'error',
      });
    } finally {
      this.finishingEncounter = false;
    }
  }

  openUpdateDocumentsDialog(templates?: Template[]): void {
    const dialogRef = this.trackDialog(this.dialog.open(UpdateDocumentsComponent, {
      data: { dictation: this.dictation, templates },
      size: 'mdPlus',
    }));
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      if (result.action === 'skip' || result.action === 'update') {
        this.router.navigate(['/document', this.dictation?._id]);
      }
    });
  }

  openTemplateSelector(templates?: Template[]): void {
    if (this.destroyed) return;
    const dialogRef = this.trackDialog(this.dialog.open(SelectTemplateComponent, {
      data: { dictation: this.dictation, templates },
      size: 'mdPlus',
    }));
    dialogRef.afterClosed().subscribe((result?: { templates?: Template[]; defaultDocumentsCreated?: boolean }) => {
      if (result) {
        for (const template of result.templates ?? []) {
          this.analytics.track('document_created', {
            encounter_id: this.dictation?._id,
            blueprint_id: String(template.id),
            surface: 'encounter',
            trigger: 'initial',
          });
        }
        this.router.navigate(['/document', this.dictation?._id]);
      }
    });
  }

  onBreadcrumbClick(): void {
    const toDocument = this.isResumedConsultation && !!this.dictation?._id;
    this.analytics.track('breadcrumb_clicked', {
      from_page: 'encounter',
      to_page: toDocument ? 'document' : 'home',
    });
    this.goBack();
  }

  goBack(): void {
    if (this.isResumedConsultation && this.dictation?._id) {
      this.router.navigate(['/document', this.dictation._id]);
      return;
    }

    this.router.navigate(['/home']);
  }

  toggleNotesPanel(): void {
    this.openContentPanel('transcript');
  }

  openContentPanel(tab: 'context' | 'transcript'): void {
    this.contentPanelTabIndex = tab === 'transcript' ? 0 : 1;
    if (this.isMobile) return;
    this.openDrawer(tab);
  }

  openDrawer(which: 'transcript' | 'context'): void {
    this.activeDrawer = which;
    this.activeNotesTab = which;
    if (this.rightDrawer) {
      this.rightDrawer.open();

      // Focus the note textarea when opening context drawer
      if (which === 'context' && !this.showEncounterContentPanel) {
        setTimeout(() => {
          this._noteTextarea?.nativeElement?.focus();
        }, 500);
      }
    }
  }

  closeDrawer(): void {
    if (this.rightDrawer) {
      this.rightDrawer.close();
    }
  }

  private applyMobileBreakpoint(isMobile: boolean): void {
    if (this.showEncounterContentPanel && this.rightDrawer?.opened) {
      this.closeDrawer();
    }
    this.isMobile = isMobile;
  }

  onDrawerClosed(): void {
    this.activeDrawer = null;
  }

  setActiveNotesTab(tab: 'transcript' | 'context'): void {
    if (tab === this.activeNotesTab) return;
    this.activeNotesTab = tab;
  }


  toggleContextAccordion(): void {
    this.isContextAccordionExpanded = !this.isContextAccordionExpanded;
  }

  addNote(): void {
    const trimmed = (this.newNote ?? '').trim();
    if (!trimmed) return;

    const timeSeconds = this.totalTime + this.currentRecordingTime;
    this.notes = [
      ...this.notes,
      {
        duration: timeSeconds * 1000,
        note: trimmed,
      },
    ];
    const dictation = this.dictation!;
    const watermark = nextContentUpdatedAt(dictation.copilot?.content_updated_at);
    dictation.copilot!.notes = this.notes;
    dictation.copilot!.content_updated_at = watermark;
    this.apiService.updateDictationCopilot(dictation._id, {
      copilot: { notes: this.notes, content_updated_at: watermark },
    }).then(() => {
      this.analytics.track('context_note_added', {
        surface: 'consultation_screen',
        encounter_id: dictation._id,
      });
    }).catch(err => {
      console.error('Error updating notes:', err);
    });
    this.newNote = '';
    this.resetTextareaHeight();

    setTimeout(() => {
      const container = this._notesListContainer?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 1);
  }

  autoResize(textarea: HTMLTextAreaElement, container: HTMLElement): void {
    textarea.style.height = 'auto';
    const styles = window.getComputedStyle(container);
    const paddingTop = parseFloat(styles.paddingTop || '0');
    const paddingBottom = parseFloat(styles.paddingBottom || '0');
    const usableHeight = Math.max(0, container.clientHeight - paddingTop - paddingBottom);

    if (textarea.scrollHeight > usableHeight) {
      textarea.style.height = `${usableHeight}px`;
      textarea.style.overflowY = 'auto';
      // Scroll down when scroll is added
      textarea.scrollTop = textarea.scrollHeight;
    } else {
      textarea.style.height = `${textarea.scrollHeight}px`;
      textarea.style.overflowY = 'hidden';
    }
  }

  resetTextareaHeight(): void {
    const textarea = this._noteTextarea?.nativeElement;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.overflowY = 'hidden';
    }
  }

  async confirmDeleteNote(index: number): Promise<void> {
    const data: ConfirmDialogData = {
      icon: 'warning',
      title: this.translate.instant('Delete note?'),
      description: this.translate.instant('Are you sure you want to delete this note?'),
      cancelText: this.translate.instant('Cancel'),
      confirmText: this.translate.instant('Delete')
    } as any;
    const dialogRef = this.trackDialog(this.dialog.open(ConfirmComponent, { data, presentation: 'dialog' }));
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) this.deleteNote(index);
    });
  }

  deleteNote(index: number): void {
    if (index < 0 || index >= this.notes.length) return;

    this.notes = this.notes.filter((_, i) => i !== index);
    const dictation = this.dictation!;
    const watermark = nextContentUpdatedAt(dictation.copilot?.content_updated_at);
    dictation.copilot!.notes = this.notes;
    dictation.copilot!.content_updated_at = watermark;
    this.apiService.updateDictationCopilot(dictation._id, {
      copilot: { notes: this.notes, content_updated_at: watermark },
    }).then(() => {
      this.analytics.track('context_note_deleted', {
        surface: 'consultation_screen',
        encounter_id: dictation._id,
      });
    }).catch(err => {
      console.error('Error updating notes:', err);
    });
  }

  // timers handled by service

  formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  get formattedRecordingTime(): string {
    // Show total time from server + current recording time
    const displayTime = this.totalTime;
    return this.formatTime(displayTime);
  }

  get recordingTimeParts(): { hours?: string; mins: string; secs: string } {
    const seconds = this.totalTime;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const parts = {
      mins: mins.toString().padStart(2, '0'),
      secs: secs.toString().padStart(2, '0'),
    };

    if (hours > 0) {
      return {
        hours: hours.toString().padStart(2, '0'),
        ...parts,
      };
    }

    return parts;
  }

  /**
   * @description The theme of the encounter based on the UI encounter state.
   * @returns The theme of the encounter.
   */
  get theme(): 'blue' | 'recording' | 'light' {
    switch (this.encounterUiState) {
      case 'recording': return 'recording';
      case 'paused': return 'blue';
      case 'initial': return 'blue';
      default:return 'blue';
    }
  }

  get isLightTheme(): boolean {
    return this.theme === 'light';
  }


  // get showOfflineStatus(): boolean {
  //   return !this.browserOnline && this.recordingState !== RecordingState.Recording;
  // }

  get isBlueTheme(): boolean {
    return this.theme === 'blue';
  }

  get topNavLogoSrc(): string {
    // SpeechWrite only ships a white lockup, so it can only be used on the dark
    // (blue/recording) themes. The light/paused theme keeps the visible T-Pro
    // light logo until a dark SpeechWrite variant is available.
    if (this.branding.isSpeechwrite && !this.isLightTheme) return this.branding.logoSrc;
    return this.isLightTheme ? '/assets/logo-light.svg' : '/assets/logo.svg';
  }

  get hasRecordedAudio(): boolean {
    return this.totalTime > 0;
  }

  get encounterUiState(): EncounterUiState {
    if (this.recordingState === RecordingState.Recording) return 'recording';
    if (this.hasRecordedAudio) return 'paused';
    return 'initial';
  }

  get transcriptTabEnabled(): boolean {
    return this.encounterUiState !== 'initial';
  }

  onPanelSaveNote(text: string): void {
    this.newNote = text;
    this.addNote();
  }

  /**
   * @description Checks if the session recording has been initialised at some point in the past or currently.
   * @returns True if the session has been initialised, false otherwise.
   */
  // get sessionInitialized(): boolean {
  //   return this.totalTime > 0 || this.recordingState === RecordingState.Recording || this.currentRecordingTime > 0;
  // }

  // amplitude processing handled by service

  private initializeCanvas(): void {
    // Avoid re-initialising if context already exists
    if (this.canvasContext) {
      return;
    }
    if (this._audioVisualizer?.nativeElement) {
      this.canvasContext = this._audioVisualizer.nativeElement.getContext('2d');
      if (this.canvasContext) {
        const canvas = this._audioVisualizer.nativeElement;
        canvas.width = this.visualizerWidth;
        canvas.height = this.visualizerHeight;
      }
    } else {
      console.error('Canvas element not found!');
    }
  }

  private startVisualization(): void {
    if (!this.canvasContext) return;
    // Avoid duplicate animation loops
    if (this.animationId) {
      return;
    }

    const draw = () => {
      if (!this.canvasContext || !this._audioVisualizer) return;

      // If not recording, show static bars at minimum height
      if (this.recordingState !== RecordingState.Recording) {
        this.currentAmplitude = 0;
      }

      const canvas = this._audioVisualizer.nativeElement;
      const width = canvas.width;
      const height = canvas.height;

      // Clear canvas completely
      this.canvasContext.clearRect(0, 0, width, height);

      const barWidth = this.visualizerBarWidth;
      const totalWidth = barWidth * this.barCount + this.barSpacing * (this.barCount - 1);
      const startX = (width - totalWidth) / 2;

      this.canvasContext.fillStyle = 'rgba(255, 255, 255, 0.8)';

      for (let i = 0; i < this.barCount; i++) {
        const x = startX + i * (barWidth + this.barSpacing);

        // Create height pattern: small, mid, big, mid, small, etc.
        // Using sine wave to create smooth height variation
        const heightMultiplier = (Math.sin((i / (this.barCount - 1)) * Math.PI) * 0.6) + 0.4; // 0.4 to 1.0

        // Create random variation for each bar based on current amplitude
        const variation = Math.random() * 0.3 + 0.7; // 0.7 to 1.0
        const barAmplitude = this.currentAmplitude * variation;

        // Calculate bar height with pattern-based maximum
        const minHeight = barWidth;
        const maxHeight = height * 0.8 * heightMultiplier;
        const barHeight = Math.max(minHeight, barAmplitude * maxHeight);

        // Center the bar vertically
        const y = (height - barHeight) / 2;

        // Pill-shaped bars: fully rounded ends
        const cornerRadius = Math.min(barWidth / 2, barHeight / 2);
        this.drawRoundedRect(x, y, barWidth, barHeight, cornerRadius);
      }

      // Continue animation
      this.animationId = requestAnimationFrame(draw);
    };

    draw();
  }

  private cleanupAudioVisualization(): void {
    // Cancel animation frame
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Clear references
    this.canvasContext = null;
    this.currentAmplitude = 0;
  }

  private drawRoundedRect(x: number, y: number, width: number, height: number, radius: number): void {
    if (!this.canvasContext) return;

    this.canvasContext.beginPath();
    this.canvasContext.moveTo(x + radius, y);
    this.canvasContext.lineTo(x + width - radius, y);
    this.canvasContext.quadraticCurveTo(x + width, y, x + width, y + radius);
    this.canvasContext.lineTo(x + width, y + height - radius);
    this.canvasContext.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    this.canvasContext.lineTo(x + radius, y + height);
    this.canvasContext.quadraticCurveTo(x, y + height, x, y + height - radius);
    this.canvasContext.lineTo(x, y + radius);
    this.canvasContext.quadraticCurveTo(x, y, x + radius, y);
    this.canvasContext.closePath();
    this.canvasContext.fill();
  }

  private updateConsentSettings(): void {
    const defaultMessage = this.translate.instant("I have the patient's consent to record this consult.");
    let require = false;
    let message = defaultMessage;
    let settings: UserSettings | EntitySettings | null = null;

    const entitySettings = this.user?.entity?.settings;
    if (entitySettings?.enableCopilotPatientConsent) {
      require = true;
      settings = entitySettings;
    } else {
      const userSettings = this.user?.settings;
      if (userSettings?.enableCopilotPatientConsent) {
        require = true;
        settings = userSettings;
      }
    }

    message = (settings?.copilotPatientConsentMessage && settings?.copilotPatientConsentMessage.trim().length > 0)
          ? settings.copilotPatientConsentMessage
          : defaultMessage;

    this.requirePatientConsent = require;
    this.patientConsentMessage = message;
    this.patientConsentGiven = !require ? true : false;
  }

  clearError(): void {
    // TODO: is it necessary to clear the error on the recorder service?
    this.recorderService.clearError();
    this.currentError = null;
    this.updateCurrentErrorToast();
  }

  private isMicrophoneError(code: RecorderErrorCode): boolean {
    return code.startsWith('MIC_');
  }

  private getRecorderErrorMessage(errorCode: RecorderErrorCode): string {
    const config = errorMEssageByCode[errorCode];
    if (!config) {
      return this.translate.instant(RECORDER_DEFAUL_ERROR_MESSAGE);
    }
    const params =
      errorCode === RecorderErrorCode.MAX_RECORDING_LENGTH_REACHED
        ? {
            limit: formatDictationDurationLimit(
              MAX_DICTATION_DURATION_SECONDS,
              (key, params) => this.translate.instant(key, params),
            ),
          }
        : undefined;
    const translated = this.translate.instant(config.key, params);
    if (translated && translated !== config.key) {
      return translated;
    }
    return this.translate.instant(config.fallback, params);
  }

  private shouldShowCurrentErrorToast(): boolean {
    return !!this.currentError;
  }

  private updateCurrentErrorToast(): void {
    if (this.currentErrorToastId) {
      this.toastProvider.dismiss(this.currentErrorToastId);
      this.currentErrorToastId = null;
    }

    if (!this.shouldShowCurrentErrorToast() || !this.currentError) {
      return;
    }

    if (this.currentError.errorCode?.startsWith('MIC_')) {
      // Access denied / no device found -> ticket-defined copy with the actionable
      // "allow access" link. Other MIC_* errors keep their specific message so the
      // toast is not misleading (e.g. "selected microphone not available").
      this.currentErrorToastId = this.toastProvider.show({
        message: this.micAccessBlocked
          ? this.translate.instant('Microphone access denied. Please grant microphone permission to record,')
          : this.currentError.message,
        variant: 'error',
        duration: 0,
        dismissible: false,
        action: {
          label: this.translate.instant('allow access'),
          onClick: () => { void this.retryMicrophoneAccess(); },
        },
      });
      return;
    }

    this.currentErrorToastId = this.toastProvider.show({
      message: this.currentError.message,
      variant: 'error',
      duration: 0,
    });
  }

  private dismissEncounterToasts(): void {
    if (this.currentErrorToastId) {
      this.toastProvider.dismiss(this.currentErrorToastId);
      this.currentErrorToastId = null;
    }

    if (this.syncingToastId) {
      this.toastProvider.dismiss(this.syncingToastId);
      this.syncingToastId = null;
    }
  }

  openPatientEditor(): void {

    const data: any = {
      dictationId: this.dictation?._id || '',
      mode: 'consultation',
      flow: 'encounter',
    };

    if (this.dictation?.copilot) {
      const copilot = this.dictation.copilot;
      // Forward any manually-entered patient field (name, id, or DOB) so partial
      // manual patients (e.g. DOB-only) round-trip into the edit dialog.
      const hasPatientData = !!(
        copilot.subject_name ||
        copilot.subject_first_name ||
        copilot.subject_last_name ||
        copilot.subject_id ||
        copilot.subject_dob
      );

      if (hasPatientData) {
        data.patient = {
          subject_name: copilot.subject_name || '',
          subject_prefix: copilot.subject_prefix || '',
          subject_first_name: copilot.subject_first_name || '',
          subject_middle_name: copilot.subject_middle_name || '',
          subject_last_name: copilot.subject_last_name || '',
          subject_id: copilot.subject_id || '',
          subject_alternative_id: copilot.subject_alternative_id || '',
          subject_dob: copilot.subject_dob || null,
          facility_id: copilot.facility_id ?? null,
        };
      }
    }
    let dialogRef: AppDialogRef<any>;
    if (this.user?.entity?.settings?.enableDictate === true) {
      dialogRef = this.trackDialog(this.dialog.open(EditPatientComponent, {
        size: 'lg',
        maxHeight: '100vh',
        data,
      }));
    } else {
      const combinedName = formatDictationSubjectName(this.dictation?.copilot);
      dialogRef = this.trackDialog(this.dialog.open(ManualPatientSelectorComponent, {
        size: 'sm',
        data: {
          from: 'edit',
          dictationId: this.dictation?._id || '',
          patientName: combinedName,
          patientId: this.dictation?.copilot?.subject_id || '',
          dateOfBirth: this.dictation?.copilot?.subject_dob || null,
        }
      }));
    }
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.saved && result?.patient && this.dictation?.copilot) {
        Object.assign(this.dictation.copilot, result.patient);
      }
    });
  }

  async onMicrophoneSelectionChange(deviceId: string): Promise<void> {
    // Defer to the recorder service: it validates the device and emits the new
    // selection on selectedMicrophone$, which drives this.selectedMicrophone.
    // This avoids showing a selection the recorder never applied.
    await this.recorderService.changeMicrophone(deviceId);
  }

  toggleMicSelector(): void {
    // Only block expansion when the mic is hard-blocked (denied / no devices).
    // Recoverable errors keep the picker usable so the user can pick another device.
    if (this.micAccessBlocked) {
      return;
    }

    this.isMicSelectorExpanded = !this.isMicSelectorExpanded;
  }

  async selectMicrophoneFromList(deviceId: string): Promise<void> {
    this.isMicSelectorExpanded = false;
    await this.onMicrophoneSelectionChange(deviceId);
  }

  async retryMicrophoneAccess(): Promise<void> {
    this.clearError();
    await this.recorderService.loadMicrophones();
  }

  uploadAudio(): void {
    // TODO: Implement audio upload functionality
    console.log('Upload audio clicked');
  }

  // Analytics helpers

  private trackConsultationDurationLimitReached(): void {
    this.analytics.track('consultation_duration_limit_reached', {
      surface: 'consultation_screen',
      total_recorded_duration_seconds: this.recorderService.getRecordedDurationSeconds(),
      ...(this.dictation?._id ? { encounter_id: this.dictation._id } : {}),
    });
  }

}
